import type {
  DocScoreRecommendation,
  DocScoreSection,
  DocumentationScore,
} from '../types/buildExpansion';

const SECTION_LABELS: Record<string, string> = {
  identity: 'Identity',
  specifications: 'Specifications',
  modifications: 'Modifications',
  media: 'Media',
  history: 'History',
  verification: 'Verification',
};

const SECTION_ORDER = [
  'identity',
  'specifications',
  'modifications',
  'media',
  'history',
  'verification',
];

/** Backend stores recommendations as plain strings; normalize for UI consumption. */
export function normalizeDocScoreRecommendations(
  raw: unknown
): DocScoreRecommendation[] {
  if (!Array.isArray(raw)) return [];

  const normalized: DocScoreRecommendation[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const text = item.trim();
      if (text) normalized.push({ section: 'general', text });
      continue;
    }
    if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      const text = String(rec.text ?? rec.message ?? rec.label ?? rec.suggestion ?? rec.title ?? '').trim();
      if (!text) continue;
      const section = String(rec.section ?? rec.key ?? 'general').trim() || 'general';
      normalized.push({ section, text });
    }
  }
  return normalized;
}

/** Backend stores sections as a keyed object; the UI expects a labeled array. */
export function normalizeDocScoreSections(raw: unknown): DocScoreSection[] {
  if (Array.isArray(raw)) {
    return raw
      .map((section) => {
        if (!section || typeof section !== 'object') return null;
        const s = section as Record<string, unknown>;
        const key = String(s.key ?? '').trim();
        const label = String(s.label ?? SECTION_LABELS[key] ?? key).trim();
        if (!key && !label) return null;
        return {
          key: key || label.toLowerCase().replace(/\s+/g, '_'),
          label: label || key,
          score: Number(s.score ?? 0),
          max: Number(s.max ?? 0),
        };
      })
      .filter((s): s is DocScoreSection => s !== null);
  }

  if (!raw || typeof raw !== 'object') return [];

  const map = raw as Record<string, { score?: number; max?: number; label?: string }>;
  const keys = [
    ...SECTION_ORDER.filter((k) => k in map),
    ...Object.keys(map).filter((k) => !SECTION_ORDER.includes(k)),
  ];

  return keys.map((key) => ({
    key,
    label: map[key]?.label?.trim() || SECTION_LABELS[key] || key,
    score: Number(map[key]?.score ?? 0),
    max: Number(map[key]?.max ?? 0),
  }));
}

/** Raw API shape before client normalization (matches PHP output). */
export type RawDocumentationScore = Omit<DocumentationScore, 'sections' | 'recommendations'> & {
  sections?: unknown;
  recommendations?: unknown;
};

/** Normalize API payloads from vehicle_by_slug.php and vehicle_documentation_score.php. */
export function normalizeDocumentationScore(
  score: RawDocumentationScore | DocumentationScore | null | undefined
): DocumentationScore | null {
  if (!score) return null;
  return {
    ...score,
    sections: normalizeDocScoreSections(score.sections),
    recommendations: normalizeDocScoreRecommendations(score.recommendations),
  };
}
